import { apiClient } from './client'

export type SupportTicketStatus =
  | 'New'
  | 'Reviewed'
  | 'Assigned'
  | 'WaitingForCustomer'
  | 'WaitingForAgent'
  | 'WaitingForDeveloper'
  | 'Resolved'
  | 'Open'
  | 'WaitingOnCustomer'
export type SupportSenderType = 'Customer' | 'Agent' | 'System'
export type SupportCategorySource = 'User' | 'Auto'

export type SupportTicketSummary = {
  id: string
  status: SupportTicketStatus
  category: string
  queue: string
  assignedAgentId?: string | null
  assignmentRevision: number
  lastMessageAt: string
  lastMessagePreview: string
  userId: string
  username?: string | null
  profileImageUrl?: string | null
  userCreatedAt?: string | null
  linkedUserIds: string[]
  tags: string[]
  subscriptionTier?: string | null
  subscriptionStatus?: string | null
}

export type SubscriptionFilter = 'all' | 'free' | 'trial' | 'paid'

export type SupportTicket = {
  id: string
  userId: string
  status: SupportTicketStatus
  category: string
  categorySource: SupportCategorySource
  queue: string
  assignedAgentId?: string | null
  assignedAt?: string | null
  assignmentRevision: number
  createdAt: string
  updatedAt: string
  resolvedAt?: string | null
  lastMessageAt: string
  lastMessagePreview: string
  linkedUserIds: string[]
  tags: string[]
  batterySaverMode?: boolean | null
  batteryLevelPercent?: number | null
}

export type SupportAttachment = {
  attachmentId: string
  fileName: string
  contentType: string
  sizeBytes: number
  downloadUrl?: string | null
}

export type SupportMessage = {
  id: string
  ticketId: string
  senderType: SupportSenderType
  body: string
  createdAt: string
  clientMessageId?: string | null
  diagnosticsIncluded: boolean
  diagnostics?: unknown
  attachments: SupportAttachment[]
  macroId?: string | null
}

export type SupportNote = {
  id: string
  ticketId: string
  authorAgentId: string
  body: string
  createdAt: string
}

export type SupportUserProfile = {
  userId: string
  username?: string | null
  contactEmail?: string | null
  profileImageUrl?: string | null
  createdAt: string
  lastLoginAt?: string | null
  subscriptionTier?: string | null
  subscriptionStatus?: string | null
}

export type SupportAgent = {
  id: string
  displayName?: string | null
  role: 'Admin' | 'SupportAdmin' | 'SupportAgent'
}

export type SupportAdminThreadResponse = {
  ticket: SupportTicket
  messages: SupportMessage[]
  notes: SupportNote[]
  user?: SupportUserProfile | null
  linkedUsers: SupportUserProfile[]
}

export type SupportMacro = {
  id: string
  title: string
  body: string
  createdByAgentId: string
  createdAt: string
  updatedAt: string
}

export type DateFilter = 'all' | 'today' | '3days' | 'week' | 'month'

export type GetTicketsParams = {
  statuses?: SupportTicketStatus[]
  queue?: string
  assignedAgentId?: string
  unassignedOnly?: boolean
  since?: DateFilter
  search?: string
  subscription?: SubscriptionFilter
  cursor?: string
  limit?: number
}

export async function getSupportTickets(params?: GetTicketsParams) {
  const searchParams = new URLSearchParams()
  params?.statuses?.forEach((status) => searchParams.append('status', status))
  if (params?.queue) searchParams.set('queue', params.queue)
  // Backend expects 'assigned' param which can be 'me', 'unassigned', or an agent ID
  if (params?.assignedAgentId) searchParams.set('assigned', params.assignedAgentId)
  if (params?.unassignedOnly) searchParams.set('assigned', 'unassigned')
  if (params?.since && params.since !== 'all') searchParams.set('since', params.since)
  if (params?.search) searchParams.set('search', params.search)
  if (params?.subscription && params.subscription !== 'all') searchParams.set('subscription', params.subscription)
  if (params?.cursor) searchParams.set('cursor', params.cursor)
  if (params?.limit) searchParams.set('limit', params.limit.toString())

  const query = searchParams.toString()
  const { data } = await apiClient.get<SupportTicketSummary[]>(`/api/admin/support/tickets${query ? `?${query}` : ''}`)
  return data
}

export async function getSupportTicket(ticketId: string) {
  const { data } = await apiClient.get<SupportAdminThreadResponse>(`/api/admin/support/tickets/${ticketId}`)
  return data
}

export async function getSupportAgents() {
  const { data } = await apiClient.get<SupportAgent[]>('/api/admin/support/agents')
  return data
}

export type SendAgentMessageRequest = {
  body: string
  status?: SupportTicketStatus
  macroId?: string
}

export async function sendAgentMessage(ticketId: string, request: SendAgentMessageRequest) {
  const { data } = await apiClient.post<SupportMessage>(`/api/admin/support/tickets/${ticketId}/messages`, request)
  return data
}

export type UpdateTicketRequest = {
  status?: SupportTicketStatus
  category?: string
  queue?: string
}

export async function updateTicket(ticketId: string, request: UpdateTicketRequest) {
  const { data } = await apiClient.post<SupportTicket>(`/api/admin/support/tickets/${ticketId}`, request)
  return data
}

export async function addTicketNote(ticketId: string, body: string) {
  const { data } = await apiClient.post<SupportNote>(`/api/admin/support/tickets/${ticketId}/notes`, { body })
  return data
}

export async function assignTicket(ticketId: string, agentId: string, expectedAssignmentRevision: number) {
  const { data } = await apiClient.post<SupportTicket>(`/api/admin/support/tickets/${ticketId}/assign`, {
    agentId,
    expectedAssignmentRevision
  })
  return data
}

export async function unassignTicket(ticketId: string, expectedAssignmentRevision: number) {
  const { data } = await apiClient.post<SupportTicket>(`/api/admin/support/tickets/${ticketId}/unassign`, {
    expectedAssignmentRevision
  })
  return data
}

export async function getSupportMacros() {
  const { data } = await apiClient.get<SupportMacro[]>('/api/admin/support/macros')
  return data
}

export async function createSupportMacro(title: string, body: string) {
  const { data } = await apiClient.post<SupportMacro>('/api/admin/support/macros', { title, body })
  return data
}

export async function updateSupportMacro(macroId: string, title: string, body: string) {
  const { data } = await apiClient.put<SupportMacro>(`/api/admin/support/macros/${macroId}`, { title, body })
  return data
}

export async function deleteSupportMacro(macroId: string) {
  await apiClient.delete(`/api/admin/support/macros/${macroId}`)
}

export async function linkUserToTicket(ticketId: string, userId: string) {
  const { data } = await apiClient.post<SupportTicket>(`/api/admin/support/tickets/${ticketId}/link`, { userId })
  return data
}

export async function unlinkUserFromTicket(ticketId: string, userId: string) {
  const { data } = await apiClient.post<SupportTicket>(`/api/admin/support/tickets/${ticketId}/unlink`, { userId })
  return data
}

export async function updateTicketTags(ticketId: string, tags: string[]) {
  const { data } = await apiClient.post<SupportTicket>(`/api/admin/support/tickets/${ticketId}/tags`, { tags })
  return data
}
